import { Injectable, TemplateRef, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  type CommandPaletteConfig,
  type SuiSimpleChatFeedbackEvent,
  type SuiSimpleChatMessage,
  type SuiCommandItem,
  type SuiCommandPaletteRef,
  SuiCommandPaletteService,
  SuiModalService,
  SuiToastService,
  type SuiThemeMode,
  SuiThemeService,
} from '@semeris/ui';
import { uniqBy } from 'lodash-es';
import { Observable, combineLatest, map, take } from 'rxjs';

import { AppSelectors } from '../../../store/app/app.selectors';
import { DocumentViewerActions } from '../../../store/document-viewer/document-viewer.actions';
import { DocumentViewerSelectors } from '../../../store/document-viewer/document-viewer.selectors';
import { ExtractSelectors } from '../../../store/extract/extract.selectors';
import { HomeSelectors } from '../../../store/home/home.selectors';
import { ManagerSelectors } from '../../../store/manager/manager.selectors';
import {
  activeSearchTool,
  buildAllToolsCategories,
  isToolEnabledInWorkspace,
  searchToolLink,
  workspaceGateVerdict,
  type MenuItem,
} from '../../../store/menu/menu.selectors';
import { type RootState } from '../../../store/root-state.model';
import { SearchSelectors } from '../../../store/search/search.selectors';
import { TransactionSelectors } from '../../../store/transaction/transaction.selectors';
import { AdminService } from '../../admin/services/admin.service';
import { AiChatService } from '../../ai-chat/services/ai-chat.service';
import { XRayOptions } from '../../document-viewer/models/document-viewer.models';
import { GuidedTourCatalogService } from '../../guided-tour/services/guided-tour-catalog.service';
import { HELP_ID_DEFAULT } from '../../help/models/help.model';
import { helpPageLink } from '../../help/utils/help-page-url';
import { type Activity } from '../../home/models/activity-item';
import { type Transaction } from '../../home/models/transaction.model';
import { type Manager } from '../../manager/models/manager.model';
import { DEFAULT_COLUMN, DEFAULT_COLUMN_WIDTH_DT } from '../../market-comparison/constants/default-column';
import { getColumnId } from '../../market-comparison/constants/get-column-id';
import { ColumnType } from '../../market-comparison/models/column-type.enum';
import { type Column, type TransactionId } from '../../market-comparison/models/column.model';
import { type DefinedTermMeta } from '../../market-comparison/models/defined-term-meta.model';
import { type SearchResultRaw } from '../../search/models/search.model';
import { SearchService } from '../../search/services/search.service';
import { SemerisRoutes } from '../../shared/constants/routes';
import { Permission, PermissionRelation } from '../../shared/models/api/permissions.enum';
import { TransactionToLinkPipe } from '../../shared/pipes/transaction-to-link.pipe';
import { LinkAccessService } from '../../shared/services/link-access.service';
import { PermissionService } from '../../shared/services/permission.service';
import { flattenObject } from '../../shared/utils/object.helpers';
import { isInWorkspaceAssetScope } from '../../shared/utils/workspace-asset-scope';
import { wsLink } from '../../shared/utils/workspace-link';
import { type DatapointField } from '../../unified-datapoint/models/datapoint-field.models';
import { type DatapointRule } from '../../unified-datapoint/models/datapoint-rule.model';
import { canOpenDatapointEditor } from '../../unified-datapoint/utils/datapoint-editor-access';
import { WorkspaceRecordService } from '../../workspace-ui/workspace-record.service';
import {
  type CmdItemData,
  type CmdDealResult,
  type CmdDatapointResult,
  type CmdTopicResult,
  type CmdManagerResult,
  type CmdChecklistResult,
  ENTITY_ICON_MAP,
} from '../models/command-palette.models';

/** A `libraryId.transactionShortName` pair — the only query shape worth an exact-deal lookup. */
const LIBRARY_TRANSACTION_ID_PATTERN = /^[^\s.]+\.[^\s.]+$/;

/** Must not collide with a `buildAllToolsCategories()` title, or the nav items merge into this group. */
const CATALOG_TOOLS_GROUP = 'Catalog Tools';

/** Shared by every tour item so "start guided tour" surfaces the list the tab would show. */
const GUIDED_TOUR_KEYWORDS = ['guided tour', 'start tour', 'walkthrough'];

@Injectable({ providedIn: 'root' })
export class CommandPaletteOrchestratorService {
  private readonly cmdPalette = inject(SuiCommandPaletteService);
  private readonly router = inject(Router);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly permissionService = inject(PermissionService);
  private readonly searchService = inject(SearchService);
  private readonly themeService = inject(SuiThemeService);
  private readonly modalService = inject(SuiModalService);
  private readonly adminService = inject(AdminService);
  private readonly toastService = inject(SuiToastService);
  private readonly aiChat = inject(AiChatService);
  private readonly tourCatalog = inject(GuidedTourCatalogService);
  private readonly linkAccess = inject(LinkAccessService);
  private readonly workspaceRecord = inject(WorkspaceRecordService).active;
  /** Mirrors the toolbar's `*hasAsset`, which offers X-Ray only in a workspace with assets. */
  private readonly selectedAssets = this.store.selectSignal(AppSelectors.selectedAssetShortNames);
  /** Asset classes the active workspace covers — drops nav results for tools it holds no data for. */
  private readonly workspaceAssets = this.store.selectSignal(AppSelectors.selectedWorkspaceAssetShortNames);
  private readonly withManagers = this.store.selectSignal(AppSelectors.selectedWorkspaceHasManagers);
  private readonly workspaceEnabledTools = this.store.selectSignal(AppSelectors.selectedWorkspaceEnabledTools);
  private readonly gatewayUnavailable = this.store.selectSignal(AppSelectors.workspaceGatewayUnavailable);
  /** For the sync selection handlers — `open()` and `onSearch` are handed the permissions instead. */
  private readonly userPermissions = toSignal(this.permissionService.permissions$, { initialValue: [] as string[] });

  private resultTemplate: TemplateRef<{ $implicit: SuiCommandItem; active: boolean }> | undefined;
  private actionsTpl: TemplateRef<void> | undefined;
  private toolbarTpl: TemplateRef<void> | undefined;
  private activeRef: SuiCommandPaletteRef | null = null;

  /* ── Public API ────────────────────────────────────────── */

  public registerTemplates(
    resultTpl: TemplateRef<{ $implicit: SuiCommandItem; active: boolean }>,
    actionsTpl: TemplateRef<void>,
    toolbarTpl?: TemplateRef<void>,
  ): void {
    this.resultTemplate = resultTpl;
    this.actionsTpl = actionsTpl;
    this.toolbarTpl = toolbarTpl;
  }

  public open(): void {
    combineLatest([
      this.permissionService.permissions$.pipe(take(1)),
      this.store.select(HomeSelectors.activities).pipe(take(1)),
      this.store.select(SearchSelectors.recentSearches).pipe(take(1)),
      this.store.select(DocumentViewerSelectors.xRayOptions).pipe(take(1)),
      this.store.select(AppSelectors.selectedWorkspaceEnabledTools).pipe(take(1)),
      this.store.select(TransactionSelectors.listWithStateRecord).pipe(take(1)),
    ])
      .pipe(take(1))
      .subscribe(([permissions, activities, recentSearches, activeXray, enabledTools, transactionLookup]) => {
        const items = this.buildItems(permissions, activities, recentSearches, activeXray, enabledTools);
        const aiPermitted = this.hasPermission(permissions, Permission.WRITE_HELP_AI);
        const config: CommandPaletteConfig = {
          items,
          placeholder: 'Search or jump to...',
          searchDebounce: 200,
          instantSearch: (query) => this.instantItems(query, transactionLookup, enabledTools),
          onSearch: (query) => this.onSearch(query, permissions, enabledTools),
          resultTemplate: this.resultTemplate,
          actions: this.actionsTpl,
          toolbar: this.toolbarTpl,
          ...(aiPermitted
            ? {
                onAiChat: (message: string, history: SuiSimpleChatMessage[]) => this.aiChat.ask(message),
                // Separately gated: the `/ai-chat` page has its own guards, so offering "Continue"
                // where it can't open would hand the user a link that bounces them.
                ...(this.linkAccess.canOpen(wsLink(SemerisRoutes.aiChat), permissions)
                  ? {
                      onAiChatContinue: () => {
                        void this.router.navigate(wsLink(SemerisRoutes.aiChat));
                      },
                    }
                  : {}),
                onAiFeedback: (event: SuiSimpleChatFeedbackEvent) => this.aiChat.rate(event.rating).subscribe(),
                onAiRetry: () => this.aiChat.newConversation(),
              }
            : {}),
        };

        const ref: SuiCommandPaletteRef = this.cmdPalette.open(config);
        this.activeRef = ref;
        ref.closed$.subscribe((item) => {
          this.activeRef = null;
          this.handleSelection(item);
        });
      });
  }

  public close(): void {
    this.activeRef?.close();
  }

  public cycleTheme(): void {
    const modes: SuiThemeMode[] = ['light', 'dark', 'system'];
    const current = this.themeService.mode();
    const next = modes[(modes.indexOf(current) + 1) % modes.length];
    this.themeService.setMode(next);
  }

  /* ── Build static items ────────────────────────────────── */

  private buildItems(
    permissions: string[],
    activities: Activity[] | undefined,
    recentSearches: string[] | undefined,
    activeXray: XRayOptions[] = [],
    enabledTools?: string[],
  ): SuiCommandItem[] {
    const items: SuiCommandItem[] = [];

    // Recent activities (top 5 that still open: a deal can leave the list, a workspace can drop the tool)
    if (activities?.length) {
      for (const a of activities.filter((activity) => this.linkAccess.canOpen(activity.link, permissions)).slice(0, 5)) {
        items.push({
          id: `recent-${a.entityType}-${a.id}`,
          label: a.displayName,
          group: 'Recent',
          icon: ENTITY_ICON_MAP[a.entityType] ?? 'clock',
          description: a.typeText,
          data: { resultType: 'recent-activity', link: a.link, extras: a.extra, entityType: a.entityType },
        });
      }
    }

    // Actions
    if (this.hasPermission(permissions, Permission.WRITE_HELP_AI)) {
      items.push({ id: '__ai__', label: 'Ask AI', group: 'Actions', icon: 'sparkle' });
    }
    items.push(
      {
        id: 'action-toggle-theme',
        label: 'Toggle theme',
        group: 'Actions',
        icon: 'moon',
        description: 'Cycle light / dark / system',
        data: { resultType: 'action', action: () => this.cycleTheme() },
      },
      {
        id: 'action-settings',
        label: 'Open settings',
        group: 'Actions',
        icon: 'gear',
        data: { resultType: 'navigation', link: wsLink('settings', 'profile') },
      },
      {
        id: 'action-user-guide',
        label: 'Open User Guide',
        group: 'Actions',
        icon: 'question',
        description: 'Product documentation, on its own page',
        keywords: ['help', 'docs', 'documentation'],
        // Tenant-scoped, and open to everyone — an unverified or blocked user can still read it.
        data: { resultType: 'navigation', link: helpPageLink(HELP_ID_DEFAULT) },
      },
    );

    // Guided tours — one item per runnable tour rather than a single expandable entry, which the
    // palette has no sub-list for. `GUIDED_TOUR_KEYWORDS` is what makes "start guided tour" find them.
    if (this.hasPermission(permissions, Permission.READ_GUIDED_TOUR)) {
      for (const section of this.tourCatalog.sections()) {
        for (const row of section.rows) {
          items.push({
            id: `tour-${row.id}`,
            label: row.label,
            group: 'Guided Tours',
            icon: 'graduation-cap',
            description: section.title,
            keywords: GUIDED_TOUR_KEYWORDS,
            data: { resultType: 'navigation', link: row.link, extras: { queryParams: row.queryParams } },
          });
        }
      }
    }

    // X-Ray toggles (own group, permission-gated, with live toggle state)
    if (this.hasPermission(permissions, Permission.READ_XRAY) && this.selectedAssets()?.length) {
      items.push(
        {
          id: 'action-xray-reflow',
          label: 'Reflow',
          group: 'X-Ray',
          icon: 'text-indent',
          description: 'Reflow complex paragraphs for readability',
          toggle: activeXray.includes(XRayOptions.Reflow),
          data: { resultType: 'action', action: () => this.toggleXray(XRayOptions.Reflow) },
        },
        {
          id: 'action-xray-legal-logic',
          label: 'Legal Logic',
          group: 'X-Ray',
          icon: 'git-branch',
          description: 'Highlight key legal logic',
          toggle: activeXray.includes(XRayOptions.LegalLogic),
          data: { resultType: 'action', action: () => this.toggleXray(XRayOptions.LegalLogic) },
        },
        {
          id: 'action-xray-entities',
          label: 'Entities',
          group: 'X-Ray',
          icon: 'highlighter-circle',
          description: 'Highlight amounts, dates, and entities',
          toggle: activeXray.includes(XRayOptions.Entities),
          data: { resultType: 'action', action: () => this.toggleXray(XRayOptions.Entities) },
        },
      );
    }

    // Catalog actions, wherever the Catalog tool itself would open
    if (this.linkAccess.canOpen(wsLink(SemerisRoutes.admin.root, SemerisRoutes.admin.catalog), permissions)) {
      items.push(
        {
          id: 'action-orphan-cleaner',
          label: 'Run orphan deal cleaner',
          group: CATALOG_TOOLS_GROUP,
          icon: 'broom',
          description: 'Delete deals left behind with no documents',
          data: { resultType: 'action', action: () => this.openOrphanCleaner() },
        },
        {
          id: 'action-csv-report',
          label: 'Run CSV report',
          group: CATALOG_TOOLS_GROUP,
          icon: 'file-csv',
          description: 'Kick off the catalog CSV export',
          data: { resultType: 'action', action: () => this.runCsvReport() },
        },
      );
    }

    // Navigation items — permission-gated AND limited to the active workspace's tools (a new-style
    // workspace only lists its own tools; legacy workspaces expose all).
    // A gated tool the workspace can't run is omitted, following the sidebar: typing a name and
    // getting an unusable result is worse than getting none. All Tools shows it with a reason.
    // An outage (`unknown`) is not a denial, so those stay listed and the guard catches the click.
    const workspaceRecord = this.workspaceRecord();
    const gatewayUnavailable = this.gatewayUnavailable();
    const workspaceAssets = this.workspaceAssets();
    for (const category of buildAllToolsCategories()) {
      for (const item of category.items) {
        if (
          !this.filterByPermissions(item, permissions) ||
          !isToolEnabledInWorkspace(item.name, enabledTools) ||
          !isInWorkspaceAssetScope(item, workspaceAssets) ||
          workspaceGateVerdict(item, workspaceRecord, gatewayUnavailable).status === 'denied'
        ) {
          continue;
        }
        items.push({
          id: `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`,
          label: item.name,
          group: category.title,
          icon: item.icon,
          description: item.description,
          data: { resultType: 'navigation', link: wsLink(...item.link), extras: item.extras },
        });
      }
    }

    return items;
  }

  /* ── Async search ──────────────────────────────────────── */

  private onSearch(query: string, permissions: string[], enabledTools?: string[]): Observable<SuiCommandItem[]> {
    return combineLatest([
      this.searchService.search(query),
      this.store.select(TransactionSelectors.listWithStateRecord).pipe(take(1)),
      this.store.select(ManagerSelectors.lookup).pipe(take(1)),
      this.store.select(ExtractSelectors.order).pipe(take(1)),
      this.store.select(SearchSelectors.recentSearches).pipe(take(1)),
    ]).pipe(
      take(1),
      map(([raw, transactionLookup, managerLookup, extractOrder, recentSearches]) =>
        this.mapSearchResults(query, raw, transactionLookup, managerLookup, extractOrder, recentSearches, permissions, enabledTools),
      ),
    );
  }

  private mapSearchResults(
    query: string,
    raw: SearchResultRaw,
    transactionLookup: Record<TransactionId, Transaction>,
    managerLookup: Record<string, Manager>,
    extractOrder: string[],
    recentSearches: string[],
    permissions: string[],
    enabledTools?: string[],
  ): SuiCommandItem[] {
    const items: SuiCommandItem[] = [];

    // Exact libraryTransactionId typed (or pasted) in → jump straight to that deal. Resolved from the
    // store rather than `raw`, so — like the Full Text shortcut below — it needs its own workspace check.
    const exactDeal = isToolEnabledInWorkspace('Deals', enabledTools)
      ? this.findDealByLibraryTransactionId(query, transactionLookup)
      : undefined;
    // Must stay the first push: the palette activates the first item, and Enter goes to the deal.
    if (exactDeal) {
      items.push(this.buildGotoDealItem(exactDeal));
    }

    // First when the workspace exposes a search screen the user can open. Entity sections below are
    // backend-gated but this frontend shortcut isn't.
    const searchTool = activeSearchTool(enabledTools, permissions);
    const canSearch = !!searchTool && this.linkAccess.canOpen(searchToolLink(searchTool), permissions);
    if (canSearch) {
      const truncated = query.length > 50 ? query.slice(0, 50) + '...' : query;
      items.push({
        id: 'fts',
        label: `Search documents for "${truncated}"`,
        group: 'Full Text',
        icon: 'file-text',
        data: { resultType: 'full-text-search', query },
      });
    }

    // Recent searches (filtered by query), only where there is a search screen to rerun them on
    if (canSearch && recentSearches?.length) {
      const lowerTerms = query.trim().toLowerCase().split(' ');
      const filtered = recentSearches
        .filter((s) => lowerTerms.every((t) => s?.toLowerCase().includes(t)))
        .slice(0, SearchService.SEARCH_RESULT_LIMT_PER_GROUP);

      for (const s of filtered) {
        items.push({
          id: `rs-${s}`,
          label: s,
          group: 'Recent Searches',
          icon: 'clock-counter-clockwise',
          data: { resultType: 'recent-search', searchTerm: s },
        });
      }
    }

    // Deals
    if (raw.transactionIds?.length) {
      for (const id of raw.transactionIds) {
        const txn = transactionLookup[id];
        if (!txn || id === exactDeal?.transactionId || !this.linkAccess.canOpen(TransactionToLinkPipe.map(txn), permissions)) {
          continue;
        }
        const descParts = [txn.managerDisplayName, txn.transactionDisplayShortName, txn.issuanceType?.toUpperCase()].filter(Boolean);
        items.push({
          id: `deal-${id}`,
          label: txn.transactionDisplayName,
          group: 'Deals',
          icon: 'briefcase',
          description: descParts.join(' · '),
          data: this.buildDealData(txn),
        });
      }
    }

    // Datapoints (fields + rules)
    const datapointItems = [
      ...(raw.datapointFields ?? []).map((field) => this.buildDatapointItem(`dp-field-${field.id}`, field, permissions)),
      ...(raw.datapointRules ?? []).map((rule) => this.buildDatapointItem(`dp-rule-${rule.id}`, rule, permissions)),
    ].filter((item): item is SuiCommandItem => !!item);
    items.push(...datapointItems);

    const marketCompareLink = wsLink(SemerisRoutes.marketComparison.root);
    const canOpenMarketCompare = this.linkAccess.canOpen(marketCompareLink, permissions);

    // Defined terms (deduplicated), each opened as its own Market Compare column
    if (canOpenMarketCompare && raw.definedTerms?.length) {
      const unique = uniqBy(
        raw.definedTerms.filter((dt) => dt.definedTerm),
        'definedTerm',
      );
      for (const dt of unique) {
        items.push({
          id: `dt-${dt.definedTerm}`,
          label: dt.definedTerm,
          group: 'Defined Terms',
          icon: 'article',
          description: `in ${dt.documentCount} documents`,
          data: {
            resultType: 'defined-term',
            link: marketCompareLink,
            extras: { queryParams: definedTermCompareParams(dt.definedTerm) },
            documentCount: dt.documentCount,
          },
        });
      }
    }

    // Topics, built-in ones included: the Topics list opens every topic by id
    if (canOpenMarketCompare && raw.topics?.length) {
      for (const topic of raw.topics) {
        const link = wsLink(SemerisRoutes.marketComparison.root, topic.id);
        const data: CmdTopicResult = {
          resultType: 'topic',
          link,
          editLink: topic.template ? undefined : link,
          builtIn: topic.builtIn,
          inMyTopics: extractOrder?.includes(topic.id),
          refId: topic.referenceNumber,
          displayShortName: topic.displayShortName,
        };
        items.push({
          id: `topic-${topic.id}`,
          label: topic.displayName,
          group: 'Topics',
          icon: 'rows',
          description: topic.description,
          data,
        });
      }
    }

    // Checklists (the detail page opens read-only for a user who can't edit, as from the Checklists list)
    if (raw.checklists?.length && this.linkAccess.canOpen(wsLink(SemerisRoutes.checklist.root), permissions)) {
      for (const cl of raw.checklists) {
        const data: CmdChecklistResult = {
          resultType: 'checklist',
          link: wsLink(SemerisRoutes.checklist.root, cl.id),
          editLink: wsLink(SemerisRoutes.checklist.root, cl.id),
          refId: cl.referenceNumber,
          shared: cl.shared,
        };
        items.push({
          id: `cl-${cl.id}`,
          label: cl.displayName,
          group: 'Checklists',
          icon: 'list-checks',
          description: cl.description,
          data,
        });
      }
    }

    // Managers, each opened as a filter on the Deals screen
    const dealFilterLink = wsLink(SemerisRoutes.dealFilter.root, SemerisRoutes.dealFilter.filter);
    if (raw.managerIds?.length && this.withManagers() && this.linkAccess.canOpen(dealFilterLink, permissions)) {
      for (const managerId of raw.managerIds) {
        const mgr = managerLookup[managerId];
        if (!mgr) {
          continue;
        }
        const data: CmdManagerResult = {
          resultType: 'manager',
          link: dealFilterLink,
          queryParams: { managers: mgr.managerId, withDefaults: true, new: true },
        };
        items.push({
          id: `mgr-${managerId}`,
          label: mgr.managerDisplayName,
          group: 'Managers',
          icon: 'buildings',
          data,
        });
      }
    }

    return items;
  }

  /**
   * Resolved on the keystroke itself, straight from the store snapshot taken when the palette opened:
   * a pasted `libraryTransactionId` is navigable on Enter without waiting for the debounced search.
   */
  private instantItems(query: string, lookup: Record<TransactionId, Transaction>, enabledTools?: string[]): SuiCommandItem[] {
    if (!isToolEnabledInWorkspace('Deals', enabledTools)) {
      return [];
    }

    const deal = this.findDealByLibraryTransactionId(query, lookup);

    return deal ? [this.buildGotoDealItem(deal)] : [];
  }

  /** The Go-to row for an exact `libraryTransactionId` — shared so the instant and searched rows can't drift. */
  private buildGotoDealItem(deal: Transaction): SuiCommandItem {
    return {
      id: `goto-deal-${deal.transactionId}`,
      label: `Go to ${deal.transactionDisplayName}`,
      group: 'Go to',
      description: [deal.managerDisplayName, deal.transactionId].filter(Boolean).join(' · '),
      data: this.buildDealData(deal),
    };
  }

  /**
   * Resolves a query that is itself a `libraryTransactionId` (case-insensitively) to its deal —
   * only when the deal can be linked to, so the Deals group never dedupes against an unusable match.
   */
  private findDealByLibraryTransactionId(query: string, lookup: Record<TransactionId, Transaction>): Transaction | undefined {
    const trimmed = query.trim();
    if (!LIBRARY_TRANSACTION_ID_PATTERN.test(trimmed)) {
      return undefined;
    }
    const lowered = trimmed.toLowerCase();
    const matchingId = lookup[trimmed] ? trimmed : Object.keys(lookup).find((id) => id.toLowerCase() === lowered);
    const deal = matchingId ? lookup[matchingId] : undefined;
    return deal && TransactionToLinkPipe.map(deal).length ? deal : undefined;
  }

  /** The deal payload the result template reads — shared so the Go-to and Deals rows can't drift. */
  private buildDealData(txn: Transaction): CmdDealResult {
    return {
      resultType: 'deal',
      link: TransactionToLinkPipe.map(txn),
      transactionId: txn.transactionId,
      qualityTrafficLight: txn.qualityTrafficLight,
      isPrivate: txn.private,
      autoPublished: txn.autoPublished,
      hasLatestIssuance: !!txn.mostRecentIssue,
      issuanceType: txn.issuanceType,
      endOfLife: txn.endOfLife,
      libraryDisplayName: txn.libraryDisplayName,
      managerDisplayName: txn.managerDisplayName,
      transactionShortName: txn.transactionDisplayShortName,
    };
  }

  /** Market Compare on Enter with the editor as the second action, or whichever of the two opens; `undefined` when neither does. */
  private buildDatapointItem(id: string, datapoint: DatapointField | DatapointRule, permissions: string[]): SuiCommandItem | undefined {
    const marketCompare = datapoint.marketComparisonRouterLink;
    const canCompare = this.linkAccess.canOpen(marketCompare?.commands, permissions);
    const canOpenEditor = canOpenDatapointEditor(datapoint) && this.linkAccess.canOpen(datapoint.routerLink, permissions);
    if (!canCompare && !canOpenEditor) {
      return undefined;
    }

    const editorLabel = datapoint.disabled ? 'View read-only' : 'Edit';
    const data: CmdDatapointResult = {
      resultType: 'datapoint',
      link: canCompare ? marketCompare.commands : datapoint.routerLink,
      extras: canCompare ? marketCompare.extras : undefined,
      displayType: datapoint.displayType,
      refId: datapoint.referenceNumber,
      displayShortName: datapoint.displayShortName,
    };

    return {
      id,
      label: datapoint.displayName,
      group: 'Datapoints',
      icon: 'database',
      description: datapoint.description,
      actionLabel: canCompare ? 'Market Compare' : editorLabel,
      secondaryAction:
        canCompare && canOpenEditor
          ? {
              label: editorLabel,
              icon: datapoint.disabled ? 'eye' : 'pencil-simple',
              run: () => void this.router.navigate(datapoint.routerLink),
            }
          : undefined,
      data,
    };
  }

  /* ── Selection handling ────────────────────────────────── */

  private handleSelection(item: SuiCommandItem | undefined): void {
    if (!item) {
      return;
    }

    // __ai__ is intercepted natively by the palette via onAiChat config;
    // it never reaches handleSelection unless the user has no AI permission.
    if (item.id === '__ai__') {
      return;
    }

    const data = item.data as CmdItemData | undefined;
    if (!data) {
      return;
    }

    switch (data.resultType) {
      case 'action':
        data.action();
        break;

      case 'navigation':
      case 'recent-activity':
      case 'datapoint':
      case 'defined-term':
        void this.router.navigate(data.link, data.extras);
        break;

      case 'deal':
      case 'topic':
      case 'checklist':
        if (data.link?.length) {
          void this.router.navigate(data.link);
        }
        break;

      case 'manager':
        void this.router.navigate(data.link, { queryParams: data.queryParams });
        break;

      case 'full-text-search':
        this.navigateToSearch(data.query);
        break;

      case 'recent-search':
        this.navigateToSearch(data.searchTerm);
        break;
    }
  }

  private navigateToSearch(query: string): void {
    const tool = activeSearchTool(this.workspaceEnabledTools(), this.userPermissions());
    if (!tool) {
      return;
    }

    void this.router.navigate(searchToolLink(tool), { queryParams: { q: query } });
  }

  /* ── X-Ray toggle ──────────────────────────────────────── */

  private toggleXray(option: XRayOptions): void {
    this.store
      .select(DocumentViewerSelectors.xRayOptions)
      .pipe(take(1))
      .subscribe((current) => {
        const next = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
        this.store.dispatch(DocumentViewerActions.SetXRayOptions({ options: next, save: true }));
      });
  }

  /* ── Catalog actions ────────────────────────────────────── */

  private openOrphanCleaner(): void {
    void import('../../toolbar/components/orphan-data-cleaner-modal/orphan-data-cleaner-modal.component')
      .then(({ OrphanDataCleanerModalComponent }) => this.modalService.open(OrphanDataCleanerModalComponent, { width: 'lg' }))
      .catch((error) => {
        console.error('Failed to load orphan-data-cleaner modal', error);
        this.toastService.error('Failed to open orphan data cleaner');
      });
  }

  private runCsvReport(): void {
    this.adminService
      .runCSVReport()
      .pipe(take(1))
      .subscribe({
        next: () => this.toastService.success('CSV report started'),
        error: () => this.toastService.error('Failed to start CSV report'),
      });
  }

  /* ── Permission helpers ────────────────────────────────── */

  private hasPermission(permissions: string[], permName: string): boolean {
    return permissions.includes(permName);
  }

  private filterByPermissions(item: MenuItem, permissions: string[]): boolean {
    if (!item.permissions?.length) {
      return true;
    }
    const relation = item.permissionsRelation ?? PermissionRelation.AND;
    return relation === PermissionRelation.OR
      ? item.permissions.some((p) => permissions.includes(p))
      : item.permissions.every((p) => permissions.includes(p));
  }
}

/** Market Compare query params that open a defined term as its own column, as Term Explorer does. */
function definedTermCompareParams(term: string): Record<string, unknown> {
  const column: Column<DefinedTermMeta> = {
    type: ColumnType.DefineTerm,
    width: DEFAULT_COLUMN_WIDTH_DT,
    value: { term, relatedTerms: [], synonyms: [] },
  };

  return flattenObject({ columns: [DEFAULT_COLUMN, { ...column, id: getColumnId(column) }] });
}
